import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  Plus,
  Trash2,
  Edit2,
  User,
  Sparkles
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const CharacterManagement = () => {
  const [characters, setCharacters] = useState([]);
  const [availableJobs, setAvailableJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChar, setEditingChar] = useState(null);
  
  const [formData, setFormData] = useState({
    job: "",
    level: "",
    name: "",
    hp: ""
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [charsRes, jobsRes] = await Promise.all([
        axios.get(`${API_URL}/api/user/characters`),
        axios.get(`${API_URL}/api/jobs`)
      ]);
      
      setCharacters(charsRes.data.characters || []);
      setAvailableJobs(jobsRes.data.jobs || {});
    } catch (error) {
      console.error("Error loading characters:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddCharacter = async () => {
    if (!formData.job || !formData.level) {
      toast.error("Please select a job and enter a level");
      return;
    }
    
    const level = parseInt(formData.level);
    if (level < 1 || level > 255) {
      toast.error("Level must be between 1 and 255");
      return;
    }
    
    const hp = parseInt(formData.hp);
    if (!hp || hp < 1 || hp > 30000) {
      toast.error("HP must be between 1 and 30,000");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/user/characters`, {
        job: formData.job,
        level: level,
        name: formData.name || null,
        hp: hp
      });
      
      toast.success("Character added!");
      setShowAddModal(false);
      setFormData({ job: "", level: "", name: "", hp: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add character");
    }
  };
  
  const handleUpdateCharacter = async () => {
    if (!editingChar) return;
    
    const level = parseInt(formData.level);
    if (level < 1 || level > 255) {
      toast.error("Level must be between 1 and 255");
      return;
    }
    
    const hp = parseInt(formData.hp);
    if (!hp || hp < 1 || hp > 30000) {
      toast.error("HP must be between 1 and 30,000");
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/user/characters/${editingChar.id}`, {
        job: formData.job,
        level: level,
        name: formData.name || null,
        hp: hp
      });
      
      toast.success("Character updated!");
      setEditingChar(null);
      setFormData({ job: "", level: "", name: "", hp: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update character");
    }
  };
  
  const handleDeleteCharacter = async (charId) => {
    if (!window.confirm("Are you sure you want to delete this character?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/user/characters/${charId}`);
      toast.success("Character deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete character");
    }
  };
  
  const openEditModal = (char) => {
    setEditingChar(char);
    setFormData({
      job: char.job,
      level: char.level.toString(),
      name: char.name || "",
      hp: char.hp ? char.hp.toString() : ""
    });
  };
  
  const jobNames = Object.keys(availableJobs);
  
  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur-sm border-primary/20">
        <CardContent className="py-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="bg-card/50 backdrop-blur-sm border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-primary flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                My Characters
              </CardTitle>
              <CardDescription>
                Add your MapleStory characters for expeditions ({characters.length}/10)
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowAddModal(true)}
              disabled={characters.length >= 10}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Character
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {characters.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No characters added yet</p>
              <p className="text-sm">Add your characters to join expeditions!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {characters.map((char) => {
                const jobInfo = availableJobs[char.job] || {};
                const skills = char.skills || jobInfo.skills || [];
                const color = char.color || jobInfo.color || "#666";
                
                return (
                  <div
                    key={char.id}
                    className="relative p-4 rounded-lg bg-background/50 border border-border hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {/* Job Icon/Color Block with Skills */}
                      <div className="flex flex-col items-center gap-2">
                        <div 
                          className="w-14 h-14 rounded-lg flex items-center justify-center shadow-lg relative overflow-hidden"
                          style={{ 
                            background: `linear-gradient(135deg, ${color} 0%, ${color}88 100%)`,
                            border: `2px solid ${color}`
                          }}
                        >
                          {/* Show first skill icon as main icon */}
                          {skills.length > 0 ? (
                            <img 
                              src={skills[0].icon} 
                              alt={skills[0].name}
                              className="w-10 h-10"
                              title={skills[0].name}
                            />
                          ) : (
                            <User className="w-8 h-8 text-white" />
                          )}
                        </div>
                        {/* Additional skill icons */}
                        {skills.length > 1 && (
                          <div className="flex gap-1">
                            {skills.slice(1).map((skill, i) => (
                              <img 
                                key={i}
                                src={skill.icon} 
                                alt={skill.name}
                                className="w-6 h-6"
                                title={skill.name}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <span className="font-bold text-foreground block truncate">{char.job}</span>
                        <p className="text-2xl font-bold" style={{ color }}>Lv. {char.level}</p>
                        {char.name && (
                          <p className="text-sm text-muted-foreground truncate">IGN: {char.name}</p>
                        )}
                        {char.hp && (
                          <p className="text-sm text-red-400">HP: {char.hp.toLocaleString()}</p>
                        )}
                        {/* Skill names */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {skills.map((skill, i) => (
                            <Badge 
                              key={i}
                              variant="outline" 
                              className="text-[10px] px-1.5 py-0"
                              style={{ borderColor: color, color }}
                            >
                              {skill.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditModal(char)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteCharacter(char.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Add/Edit Character Modal */}
      <Dialog open={showAddModal || !!editingChar} onOpenChange={(open) => {
        if (!open) {
          setShowAddModal(false);
          setEditingChar(null);
          setFormData({ job: "", level: "", name: "", hp: "" });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingChar ? "Edit Character" : "Add Character"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Job Class *</Label>
              <Select value={formData.job} onValueChange={(v) => setFormData({ ...formData, job: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a 4th job class" />
                </SelectTrigger>
                <SelectContent>
                  {jobNames.map((job) => {
                    const info = availableJobs[job] || {};
                    const skills = info.skills || [];
                    return (
                      <SelectItem key={job} value={job}>
                        <span className="flex items-center gap-2">
                          {skills.length > 0 && (
                            <img src={skills[0].icon} alt="" className="w-5 h-5" />
                          )}
                          <span>{job}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {/* Show skills for selected job */}
              {formData.job && availableJobs[formData.job] && (
                <div className="mt-2 flex items-center gap-2 p-2 bg-muted/50 rounded">
                  <span className="text-xs text-muted-foreground">Skills:</span>
                  {availableJobs[formData.job].skills?.map((skill, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <img src={skill.icon} alt={skill.name} className="w-6 h-6" />
                      <span className="text-xs">{skill.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <Label>Level *</Label>
              <Input
                type="number"
                min={1}
                max={255}
                placeholder="e.g., 200"
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Character Name (Optional)</Label>
              <Input
                placeholder="Your IGN"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional: Add your in-game name for easier identification
              </p>
            </div>
            
            <div>
              <Label>HP *</Label>
              <Input
                type="number"
                min={1}
                max={30000}
                placeholder="e.g., 9999"
                value={formData.hp}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || (parseInt(val) >= 0 && parseInt(val) <= 30000)) {
                    setFormData({ ...formData, hp: val });
                  }
                }}
                data-testid="character-hp-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your character's max HP (1 - 30,000)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => {
              setShowAddModal(false);
              setEditingChar(null);
              setFormData({ job: "", level: "", name: "", hp: "" });
            }}>
              Cancel
            </Button>
            <Button onClick={editingChar ? handleUpdateCharacter : handleAddCharacter}>
              {editingChar ? "Save Changes" : "Add Character"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CharacterManagement;
